/* eslint-disable react-hooks/exhaustive-deps */
"use client"
import { useState, useEffect, useRef } from "react"
import { Bar } from "react-chartjs-2"
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend, type ChartOptions } from "chart.js"
import { Box, IconButton, Typography } from "@mui/material"
import { FirstPage, LastPage, NavigateBefore, NavigateNext } from "@mui/icons-material"

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

interface HorizontalBarChartProps {
  title: string
  data: {
    id: string
    label: string
    value: number
    color?: string
  }[]
  selectedIndex: number
  onSelect: (index: number) => void
  enablePagination?: boolean
  itemsPerPage?: number
  barThickness?: number
  showDecimals?: boolean
}

export function HorizontalBarChart({
  title,
  data,
  selectedIndex,
  onSelect,
  enablePagination = false,
  itemsPerPage = 10,
  barThickness,
  showDecimals = true,
}: HorizontalBarChartProps) {
  const [startOffset, setStartOffset] = useState(0)
  const chartRef = useRef<any>(null)

  const totalItems = data.length
  const endOffset = Math.min(startOffset + itemsPerPage, totalItems)
  const paginatedData = data.slice(startOffset, endOffset)
  const showPagination = enablePagination && totalItems > itemsPerPage

  const formatValue = (value: number) => {
    if (showDecimals) {
      return value.toLocaleString('en-US', { maximumFractionDigits: 2 })
    } else {
      return value.toLocaleString('en-US', { maximumFractionDigits: 0 })
    }
  }

  useEffect(() => {
    setStartOffset(0)
  }, [data.length])

  const handleNextItem = () => {
    if (startOffset + itemsPerPage < totalItems) {
      setStartOffset(startOffset + 1)
    }
  }

  const handlePrevItem = () => {
    if (startOffset > 0) {
      setStartOffset(startOffset - 1)
    }
  }

  const handleNext10Items = () => {
    const newOffset = Math.min(startOffset + 10, totalItems - itemsPerPage)
    setStartOffset(Math.max(0, newOffset))
  }

  const handlePrev10Items = () => {
    const newOffset = Math.max(startOffset - 10, 0)
    setStartOffset(newOffset)
  }

  const chartData = {
    labels: paginatedData.map((item) => item.label),
    datasets: [
      {
        data: paginatedData.map((item) => item.value),
        backgroundColor: paginatedData.map((item, index) => {
          const actualIndex = startOffset + index
          return actualIndex === selectedIndex ? "rgba(20, 124, 157, 0.1)" : item.color || "rgba(99, 102, 241, 0.6)"
        }),
        borderColor: paginatedData.map((item, index) => {
          const actualIndex = startOffset + index
          return actualIndex === selectedIndex ? "rgba(20, 124, 157, 1)" : "transparent"
        }),
        borderWidth: 2,
        barThickness: barThickness,
      },
    ],
  }

  const options: ChartOptions<"bar"> = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const actualIndex = startOffset + elements[0].index
        onSelect(actualIndex)
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => formatValue(context.parsed.x as number),
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { display: true, color: "rgba(0, 0, 0, 0.05)" },
        ticks: {
          callback: (value) => formatValue(Number(value)),
        },
      },
      y: { grid: { display: false } },
    },
  }

  const drawValueLabels = () => {
    if (!chartRef.current) return

    const chart = chartRef.current
    const canvas = chart.canvas
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const meta = chart.getDatasetMeta(0)
    if (!meta || !meta.data) return

    const dataset = chart.data.datasets[0]
    const values = dataset.data

    ctx.save()
    ctx.font = "bold 12px sans-serif"
    ctx.fillStyle = "#000000"
    ctx.textBaseline = "middle"
    ctx.textAlign = "left"

    meta.data.forEach((bar: any, index: number) => {
      const value = values[index]
      if (value === undefined || value === null) return

      const barStartX = bar.x
      const y = bar.y

      const formattedValue = formatValue(value)

      let textX = barStartX + 2
      const maxX = chart.scales.x.getPixelForValue(chart.scales.x.max)
      const textWidth = ctx.measureText(formattedValue).width

      if (textX + textWidth > maxX - 5) {
        textX = maxX - textWidth - 5
      }

      ctx.fillText(formattedValue, textX, y)
    })

    ctx.restore()
  }

  useEffect(() => {
    if (!chartRef.current) return

    const chart = chartRef.current
    const originalDraw = chart.draw
    chart.draw = function (...args: any[]) {
      originalDraw.apply(this, args)
      drawValueLabels()
    }
    chart.draw()

    return () => {
      if (chart.draw !== originalDraw) {
        chart.draw = originalDraw
      }
    }
  }, [chartRef.current])

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.update()
    }
  }, [startOffset, data, showDecimals])

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: "#2563eb", fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Bar ref={chartRef} data={chartData} options={options} />
      </Box>

      {showPagination && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mt: 2 }}>
          <IconButton size="small" onClick={handlePrev10Items} disabled={startOffset === 0} title="Retroceder 10 items">
            <FirstPage fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handlePrevItem} disabled={startOffset === 0} title="Retroceder 1 item">
            <NavigateBefore fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: "120px", textAlign: "center" }}>
            {startOffset + 1} - {endOffset} de {totalItems}
          </Typography>
          <IconButton
            size="small"
            onClick={handleNextItem}
            disabled={startOffset + itemsPerPage >= totalItems}
            title="Avanzar 1 item"
          >
            <NavigateNext fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleNext10Items}
            disabled={startOffset + itemsPerPage >= totalItems}
            title="Avanzar 10 items"
          >
            <LastPage fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  )
}